import { useState } from 'react'
import { useGate } from './gate'
import { resolveView, type View } from './routing'

// Provisional shell only. No section of SPEC-PARTNER-U1-U3.md carries
// [APPROVED], so no finished screen is built here: BUILD-ORDER allows plumbing,
// scaffold, routing, and logic now, and visible surfaces only from approved
// sections. Each view below is a labelled placeholder that names the state and
// points at the spec section that will supply its real copy and layout once the
// founder approves it. No governed canonical-language fragment appears yet
// (none are expected in U1 to U3, and @you-identity/contracts is not
// installable yet: B8). No login UI and no data collection anywhere, by rule.

function Placeholder(props: { view: string; note: string; specSection: string }) {
  return (
    <main className="panel" data-view={props.view}>
      <p className="eyebrow">partner shell</p>
      <h1>{props.view}</h1>
      <p>{props.note}</p>
      <p className="hold">
        Finished screen held pending spec {props.specSection} marked APPROVED.
      </p>
    </main>
  )
}

function currentPath(): string {
  return typeof window === 'undefined' ? '/' : window.location.pathname
}

export default function App() {
  const [attempt, setAttempt] = useState(0)
  const { session, authz } = useGate(attempt)
  const view: View = resolveView(currentPath(), session, authz)

  switch (view) {
    case 'loading':
      // Spec 2: a canvas skeleton, no spinners. Structural placeholder for now.
      return <main className="panel skeleton" aria-busy="true" />

    case 'home':
      return (
        <Placeholder
          view="home"
          note="Authorized. The dashboard and partner tools are U4 and U5, gated on the /v1 contract and not started."
          specSection="section 2 (shell) and later U4/U5 specs"
        />
      )

    case 'signed-out':
      return (
        <Placeholder
          view="signed-out"
          note="No verified session. The finished screen links to the For Business login on whoyouare.ai; that URL is open question 1 and the app never auto-redirects to an unconfirmed URL."
          specSection="section 3.2"
        />
      )

    case 'unavailable':
      return (
        <Placeholder
          view="unavailable"
          note="Partner sign-in is not configured yet. This is the live state until the session public key is provisioned; it never blames the user."
          specSection="section 3.2"
        />
      )

    case 'access':
      return (
        <Placeholder
          view="access"
          note="Authenticated, but not part of a partner organization. Requesting access is a link out to web intake, never a form here: the partner app collects no details."
          specSection="section 4.2"
        />
      )

    case 'pending':
      return (
        <Placeholder
          view="pending"
          note="The organization is under review. No action beyond a link back to whoyouare.ai."
          specSection="section 4.2"
        />
      )

    case 'error':
      return (
        <main className="panel" data-view="error">
          <p className="eyebrow">partner shell</p>
          <h1>error</h1>
          <p>Could not check access.</p>
          <button type="button" onClick={() => setAttempt((n) => n + 1)}>
            Try again
          </button>
        </main>
      )

    case 'not-found':
      return <Placeholder view="not-found" note="No such route." specSection="section 1" />
  }
}
